import {
  Button,
  ComboBoxDropDown,
  DropDown,
  Modal,
  MultiSelect,
  TextBox,
  ToggleSwitch,
  ValidationProvider,
  useValidation,
} from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DueDateRuleContent } from "../../components/TaskTemplateModal/DueDateRuleContent";
import {
  useCreateTaskTemplate,
  useUpdateRestoreDefault,
  useUpdateTaskTemplate,
} from "../../data/mutations/taskTemplateMutations";
import { useAgencies } from "../../data/queries/agencyQueries";
import { useTags } from "../../data/queries/tagQueries";
import { useTaskTemplate } from "../../data/queries/taskTemplateQueries";
import { useTaskType } from "../../data/queries/taskTypeQueries";
import useEvent from "../../hooks/useEvent";
import useModalEvent from "../../hooks/useModalEvent";
import Enums from "../../types/enums";

export const TaskTemplateModal = () => {
  return (
    <ValidationProvider>
      <InnerTaskTemplateModal />
    </ValidationProvider>
  );
};

const InnerTaskTemplateModal = () => {
  const { t } = useTranslation(["TaskTemplateModal"], {
    useSuspense: false,
  });

  const modalRef = useRef(null);
  const nameRef = useRef(null);
  const formRef = useRef(null);
  const activeRef = useRef(null);
  const descriptionRef = useRef(null);
  const formTypeRef = useRef(null);
  const agencyRef = useRef(null);
  const tagsRef = useRef(null);
  const dueDateRule = useRef();

  const queryClient = useQueryClient();

  const { taskTypeId } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [persistentData, setPersistentData] = useState({});

  const isUpdate = templateId !== null;

  const { data: taskType } = useTaskType(taskTypeId);
  const { data: tags } = useTags();
  const { data: agencies } = useAgencies();
  const { data: template, isPending: isTemplatePending } =
    useTaskTemplate(templateId);

  const { mutate: createTemplate } = useCreateTaskTemplate();
  const { mutate: updateTemplate } = useUpdateTaskTemplate();
  const { mutate: updateRestoreDefault } = useUpdateRestoreDefault();

  const { Events, toggleEvent, useRegisterCustomEvent } = useEvent();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const { clearValidationErrors } = useValidation();

  useRegisterEvent(ModalEvents.TaskTemplateModal, toggleModal);
  useRegisterCustomEvent(Events.UpdateTaskTemplate, (e) => {
    const { templateId } = e.detail;
    setTemplateId(templateId);
    openModal();
  });

  const id = ModalEvents.TaskTemplateModal.Id;
  const ids = {
    name: id + "_name-input",
    form: id + "_form-input",
    active: id + "_active-checkbox",
    description: id + "_description-input",
    tags: id + "_tags-input",
    formType: id + "_form-type-dropdown",
    dueDateRule: id + "_duedate-rule",
    restoreButton: id + "_restore-button",
    cancelButton: id + "_cancel-button",
    submitButton: id + "_submit-button",
    agency: id + "_agency_input",
  };

  const formatTags = (currValue, tagData) => {
    const existingTags = tagData?.filter((t) =>
      currValue.map((y) => y.Id).includes(t.Id)
    );
    const newTags = currValue?.filter(
      (value) => !tagData.some((item) => item.Id === value.Id)
    );

    newTags.forEach((value) => {
      existingTags.push({ Title: value.Title });
    });

    return existingTags;
  };

  const formatAgency = (currValue) => {
    if(currValue?.Id === currValue?.Name)
      return { Name: currValue?.Name }
    else
      return {Id: currValue?.Id, Name: currValue?.Name }
  };


  const handleDueDateRuleChange = useCallback((newDueDateRule) => {
    dueDateRule.current = newDueDateRule;
  }, []);

  function closeModal() {
    clearValidationErrors();
    setTemplateId(null);
    setPersistentData({});
    setShowModal(false);
  }

  function openModal() {
    setShowModal(true);
  }

  function toggleModal() {
    if (showModal) {
      closeModal();
    } else {
      openModal();
    }
  }

  useEffect(() => {
    if (!isTemplatePending && template) {
      setPersistentData({ ...template, DueDateRule: template.DueDateInfo });
    }
  }, [isTemplatePending]);

  const ModalContent = () => {
    const { setValidationErrors } = useValidation();

    const TemplateName = () => {
      return (
        <TextBox
          name="Name"
          ref={nameRef}
          id={ids.name}
          type="text"
          labelText={t("Modal.Name")}
          showClearButton
          className="font-awesome-input-placeholder"
          value={persistentData?.Name}
          change={() => {
            //clearValidationErrors();
          }}
        />
      );
    };

    const TemplateForm = () => {
      return (
        <TextBox
          name="Form"
          readonly={template?.Locked}
          ref={formRef}
          id={ids.form}
          type="text"
          labelText={t("Modal.Form")}
          showClearButton
          className="font-awesome-input-placeholder"
          value={persistentData?.Form}
          change={() => {
            //clearValidationErrors();
          }}
        />
      );
    };

    const TemplateDescription = ({ multiline = false, rows = null }) => {
      return (
        <TextBox
          name="Description"
          ref={descriptionRef}
          id={ids.description}
          type="text"
          labelText={t("Modal.Description")}
          showClearButton
          className="font-awesome-input-placeholder"
          value={persistentData?.Description}
          multiline={multiline}
          rows={rows}
          change={() => {
            //clearValidationErrors();
          }}
        />
      );
    };

    const TemplateFormType = () => {
      return (
        <DropDown
          id={ids.formType}
          ref={formTypeRef}
          name="FormType"
          readonly={template?.Locked}
          fields={{ text: "Name", value: "Id" }}
          dataSource={[
            { Name: t("Modal.Individual"), Id: 0 },
            { Name: t("Modal.Corporation"), Id: 1 },
            { Name: t("Modal.PayrollAndWitholding"), Id: 2 },
          ]}
          htmlFor="default-form-type"
          label={t("Modal.FormType")}
          errorMessage={t("ErrorMsg")}
          value={persistentData?.FormType}
        />
      );
    };

    const AgencyTemplate = () => {
      const value = ! persistentData?.Agency?.Id  && persistentData?.Agency?.Name
        ? {Id:persistentData?.Agency?.Name, Name:persistentData?.Agency?.Name} 
        : persistentData.Agency;
      
      if(!taskType?.Type === Enums.TaskType.TaxReturn)
        return;
      
      return (
        <ComboBoxDropDown
          allowFiltering
          id={ids.agency}
          ref={agencyRef}
          allowObjectBinding
          maxLength={"100"}
          allowCustom
          name="Agency"
          readonly={template?.Locked}
          fields={{ text: "Name", value: "Id" }}
          dataSource={agencies ?? []}
          htmlFor="agency"
          label={t("Modal.Agency")}
          value={value}
        />
      );
    };

    const ActiveTemplate = () => {
      return (
        <div className="h-fit pt-2">
          <div className=" d-flex align-items-center gap-2">
            <ToggleSwitch
              id={ids.active}
              name={"Active"}
              ref={activeRef}
              value={persistentData?.IsActive ?? true}
              checked={persistentData?.IsActive ?? true}
            />
            <span className="align-self-start active-title">
              {t("Modal.Active")}
            </span>
          </div>
        </div>
      );
    };

    const TemplateTagMultiSelect = () => {
      const onRemoving = (args) => {
        const isLocked = persistentData.Tags?.find(
          (x) => x.Id == args.itemData.Id
        )?.Locked;
        args.cancel = isLocked;
      };

      const valueTemplate = (props) => {
        const isLocked = persistentData.Tags?.find(
          (x) => x.Id == props.Id
        )?.Locked;

        return (
          <div className={`${isLocked ? "locked-chip" : ""}`}>
            {props.Title}
          </div>
        );
      };

      // If someone refreshes the grid manually (like when sorting) after an import that creates tags, the multiselect would show the id instead of the title
      // We can't invalidate the query everytime the user sorts
      // However, there is no reason why a template would have a tag that doesn't exists, so we can just add it to the data source
      // (ygonzalez)
      let notLoaded = persistentData.Tags?.filter?.(
        (x) => !tags.find((tag) => tag.Id == x.Id)
      );
      let dataSource = tags;
      if (notLoaded && notLoaded.length > 0) {
        dataSource = [...tags, ...notLoaded];
      }

      useEffect(() => {
        if (notLoaded && notLoaded.length > 0) {
          queryClient.invalidateQueries["get-tags"];
        }
      }, [tags]);

      return (
        <MultiSelect
          id={ids.tags}
          ref={tagsRef}
          name="Tags"
          dataSource={dataSource ?? []}
          fields={{ text: "Title", value: "Id" }}
          label={t("Modal.Tags")}
          value={persistentData.Tags}
          htmlFor={ids.tags}
          allowCustomValues
          allowObjectBinding
          chipSelection={() => {
            tagsRef.current?.setShowError(false);
          }}
          removing={onRemoving}
          valueTemplate={valueTemplate}
        />
      );
    };

    function onSaveTemplate() {
      let taskTemplate = {
        TaskTypeId: template?.TaskTypeId || taskTypeId,
        Type: taskType?.Type,
        Name: nameRef.current?.getValue(),
        Form: formRef.current?.getValue(),
        Description: descriptionRef.current?.getValue(),
        IsActive: activeRef.current?.checked,
        Tags: formatTags(tagsRef.current.getValue(), tags),
        FormType: formTypeRef.current?.getValue(),
        DueDateRule: dueDateRule.current,
        Agency: formatAgency(agencyRef.current?.getValue()),
        ...(isUpdate && { Id: templateId }),
      };

      setPersistentData({
        ...taskTemplate,
      });

      const mutation = isUpdate ? updateTemplate : createTemplate;

      mutation(
        { taskTemplate },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(["get-task-templates"], taskTypeId);
            toast.success(
              isUpdate
                ? t("Modal.UpdSuccessMessage")
                : t("Modal.SuccessMessage"),
              {
                position: "bottom-left",
              }
            );
            modalRef.current.close();
            toggleEvent(Events.RefreshTaskTemplateGrid);
          },
          onError: (errors) => {
            setValidationErrors(errors?.data);
          },
        }
      );
    }

    const onRestoreDefault = () => {
      var request = {
        TaskTypeId: taskTypeId,
        TemplateId: templateId,
      };

      updateRestoreDefault(
        { restoreDefaultReq: request },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(["get-task-templates"], taskTypeId);
            closeModal();
            toast.success(t("Modal.UpdSuccessMessage"), {
              position: "bottom-left",
            });
            toggleEvent(Events.RefreshTaskTemplateGrid);
          },
          onError: (errors) => {
            setValidationErrors(errors?.data);
          },
        }
      );
    };

    return (
      <div className="et-tasktemplate-modal">
        <div className="et-tasktemplate-details rounded border align-items-center ">
          <div className="et-tasktemplate-details-header gap-4 d-flex bg-et-lightest-gray border-bottom border-et-light-gray align-items-center px-4">
            <div className="flex-grow-1">
              <span className="fw-600 align-middle align-middle">
                {t("Modal.Details")}
              </span>
            </div>
            <ActiveTemplate />
            {isUpdate && template?.Locked && (
              <div className="col-3 align-self-end border-start border-et-light-gray">
                <Button
                  id={ids.restoreButton}
                  text={t("Modal.RestoreDefault")}
                  className="btn-tertiary"
                  iconCss="fa-regular fa-arrow-rotate-left"
                  onClick={onRestoreDefault}
                />
              </div>
            )}
          </div>
          <div className="p-4">
            {taskType?.Type == Enums.TaskType.TaxReturn ? (
              <>
                <div className="row">
                  <div className="col-3">
                    <TemplateForm />
                  </div>
                  <div className="col-6">
                    <TemplateFormType />
                  </div>
                  <div className="col-3">
                    <AgencyTemplate />
                  </div>
                </div>
                <div className="my-3 row">
                  <TemplateName />
                </div>
                <div className="my-3 row">
                  <TemplateDescription />
                </div>
                <div className="my-3 row">
                  <TemplateTagMultiSelect />
                </div>
              </>
            ) : (
              <>
                <div className="row">
                  <TemplateName />
                </div>
                <div className="row mt-3">
                  <TemplateDescription multiline={true} rows={3} />
                </div>
                <div className="row mt-3">
                  <TemplateTagMultiSelect />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col mt-4">
            <DueDateRuleContent
              id={ids.dueDateRule}
              onChange={handleDueDateRuleChange}
              initialValue={persistentData?.DueDateRule}
              taskType={taskType}
            />
          </div>
          <p className="mt-2">
            <span className="fw-600">{t("DateContainers.ExpertTip")} </span>
            {t("DateContainers.TipText")}
          </p>
        </div>

        <div className="d-flex justify-content-end create-template-modal-btns mx-auto gap-3">
          <Button
            id={ids.cancelButton}
            text={t("Modal.CancelBtn")}
            className="btn-tertiary"
            onClick={() => modalRef.current.close()}
          />
          <Button
            id={ids.submitButton}
            text={isUpdate ? t("Modal.UpdateBtn") : t("Modal.CreateBtn")}
            className="btn btn-primary"
            onClick={onSaveTemplate}
            loading={false}
          />
        </div>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <Modal
      ref={modalRef}
      id={ModalEvents.TaskTemplateModal.Id}
      isModal
      customHeader={() => (
        <span className="fw-700 fs-16">{t("Modal.Title")}</span>
      )}
      showCloseIcon={true}
      visible={showModal}
      content={<ModalContent />}
      close={closeModal}
      minHeight="20vh"
      width="950px"
    ></Modal>,
    document.body
  );
};
